// Pay-now dialog for a subscription invoice. Offers exactly the methods the server
// says are available: "M-Pesa" (direct Vodacom M-Pesa push into KaziPeople's own
// account) and "Bank (CRDB / NMB)" (bank checkout authorized with the bank app's
// one-time code). With neither, it says plainly that online payment is not
// available — never a broken form.
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Loader2, Smartphone, Landmark, X, XCircle } from 'lucide-react';
import { PhoneInput } from '@/features/shared/components/PhoneInput';
import { isValidPhone, PHONE_HINT } from '@/features/shared/validation/phone';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getInvoice, getPaymentAttempt, payInvoice,
  type Invoice, type PaymentOptions, type PayMethod,
} from '@/services/billingApi';
import { payAttemptView, isSandboxTestNumber, type PayView } from './payAttemptView';

const money = (v: any, cur = 'TZS') =>
  `${cur} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const BANK_APP: Record<string, string> = { CRDB: 'SimBanking', NMB: 'NMB Mkononi' };

interface Props {
  invoice: Invoice;
  options: PaymentOptions | null;
  optionsError: string | null;
  onClose: () => void;
  onPaid: () => void;
}

export const PayInvoiceDialog: React.FC<Props> = ({ invoice, options, optionsError, onClose, onPaid }) => {
  const { t } = useTranslation('admin');
  const methods = options?.methods ?? [];
  const [method, setMethod] = useState<PayMethod['method'] | null>(methods[0]?.method ?? null);
  const current = methods.find((m) => m.method === method) ?? methods[0];
  const sandbox = current?.method === 'mpesa' && current.environment === 'sandbox';

  const [phone, setPhone] = useState('');
  const [bank, setBank] = useState<'CRDB' | 'NMB'>('CRDB');
  const [account, setAccount] = useState('');
  const [otp, setOtp] = useState('');
  const [stage, setStage] = useState<'form' | 'sent'>('form');
  const [view, setView] = useState<PayView>({ kind: 'waiting' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [stillWaiting, setStillWaiting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!method && methods[0]) setMethod(methods[0].method);
  }, [methods, method]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  // Direct M-Pesa: poll the ATTEMPT (pending → success / failed / unknown).
  const pollAttempt = (paymentId: string) => {
    stopPoll();
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const a = await getPaymentAttempt(paymentId);
        const v = payAttemptView(a.status, a.reason);
        setView(v);
        if (v.kind === 'paid') { stopPoll(); onPaid(); return; }
        if (v.kind === 'failed') { stopPoll(); return; }
      } catch { /* transient — keep polling; never read as paid or failed */ }
      if (tries >= 75) { stopPoll(); setStillWaiting(true); } // ~5 min
    }, 4000);
  };

  // Bank (AzamPay callback-driven): poll the invoice until it clears.
  const pollInvoice = () => {
    stopPoll();
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const inv = await getInvoice(invoice.id);
        if (inv.status === 'paid' || Number(inv.balanceDue) <= 0) { stopPoll(); setView({ kind: 'paid' }); onPaid(); return; }
      } catch { /* keep polling */ }
      if (tries >= 38) { stopPoll(); setStillWaiting(true); }
    }, 4000);
  };

  const submit = async () => {
    if (!current) return;
    setErr(null);
    let body: Parameters<typeof payInvoice>[1];
    if (current.method === 'mpesa') {
      if (!phone.trim()) { setErr(t('billing_pay.phone_required')); return; }
      if (!(sandbox && isSandboxTestNumber(phone)) && !isValidPhone(phone)) { setErr(PHONE_HINT); return; }
      body = { phoneNumber: phone.trim(), network: 'mpesa' };
    } else {
      if (account.replace(/\s/g, '').length < 6) { setErr(t('billing_pay.account_required')); return; }
      if (!otp.trim()) { setErr(t('billing_pay.otp_required')); return; }
      body = { phoneNumber: account.replace(/\s/g, ''), network: bank.toLowerCase(), bankOtp: otp.trim() };
    }
    setSubmitting(true);
    try {
      const res = await payInvoice(invoice.id, body);
      if (res.flow === 'redirect' && res.checkoutUrl) { window.location.href = res.checkoutUrl; return; }
      setStage('sent');
      setStillWaiting(false);
      setNote(res.message ?? null);
      const v = payAttemptView(res.status ?? 'pending');
      setView(v.kind === 'paid' || v.kind === 'failed' ? v : { kind: v.kind });
      if (res.provider === 'MPESA') pollAttempt(res.paymentId);
      else pollInvoice();
    } catch (e) {
      // The server explains every refusal (busy attempt, bad number, not available…).
      setErr(getApiErrorMessage(e, t('billing_pay.start_failed')));
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => { stopPoll(); setStage('form'); setView({ kind: 'waiting' }); setOtp(''); };
  const close = () => { stopPoll(); onClose(); };

  const input = 'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100 dark:focus:ring-orange-500/20';

  let bodyEl: React.ReactNode;
  if (!options && !optionsError) {
    bodyEl = (
      <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      </div>
    );
  } else if (optionsError || !options?.available || methods.length === 0) {
    bodyEl = (
      <div className="rounded-lg border border-warning bg-warning-soft px-3 py-3 text-sm text-warning-strong">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <div className="font-medium">{optionsError || options?.message || t('billing_pay.not_available')}</div>
            <div className="mt-1 text-xs">{t('billing_pay.not_available_hint')}</div>
          </div>
        </div>
      </div>
    );
  } else if (stage === 'form') {
    bodyEl = (
      <>
        {methods.length > 1 && (
          <div className="mb-3">
            <div className="mb-1 text-sm font-medium text-foreground">{t('billing_pay.method_label')}</div>
            <div className="grid grid-cols-2 gap-2" role="radiogroup">
              {methods.map((m) => (
                <button key={m.method} type="button" role="radio" aria-checked={current?.method === m.method}
                  onClick={() => { setMethod(m.method); setErr(null); }}
                  className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-sm font-medium ${current?.method === m.method ? 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-500/[0.12] dark:text-orange-300' : 'border-border text-foreground hover:bg-muted'}`}>
                  {m.method === 'mpesa' ? <Smartphone className="h-4 w-4" /> : <Landmark className="h-4 w-4" />}
                  {m.method === 'mpesa' ? t('billing_pay.method_mpesa') : t('billing_pay.method_bank')}
                </button>
              ))}
            </div>
          </div>
        )}

        {current?.method === 'mpesa' ? (
          <>
            <p className="mb-3 text-sm text-muted-foreground">{t('billing_pay.mpesa_line')}</p>
            <label className="mb-1 block text-sm font-medium text-foreground">{t('billing_pay.phone_label')}</label>
            {sandbox ? (
              <>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="numeric" placeholder="000000000001" className={input} />
                <p className="mt-1 text-xs text-muted-foreground">{t('billing_pay.sandbox_hint')}</p>
              </>
            ) : (
              <PhoneInput value={phone} onChange={setPhone} />
            )}
          </>
        ) : (
          <>
            <label className="mb-1 block text-sm font-medium text-foreground">{t('billing_pay.bank_label')}</label>
            <select value={bank} onChange={(e) => setBank(e.target.value as 'CRDB' | 'NMB')} className={`mb-3 ${input}`}>
              {(current?.banks ?? ['CRDB', 'NMB']).map((b) => <option key={b} value={b}>{b === 'CRDB' ? 'CRDB Bank' : 'NMB Bank'}</option>)}
            </select>
            <label className="mb-1 block text-sm font-medium text-foreground">{t('billing_pay.account_label')}</label>
            <input value={account} onChange={(e) => setAccount(e.target.value)} inputMode="numeric" className={`mb-3 ${input}`} />
            <label className="mb-1 block text-sm font-medium text-foreground">{t('billing_pay.otp_label')}</label>
            <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^\d]/g, '').slice(0, 8))} inputMode="numeric" className={input} />
            <p className="mt-1 text-xs text-muted-foreground">{t('billing_pay.otp_hint', { app: BANK_APP[bank] })}</p>
          </>
        )}
        {err && <p className="mt-2 text-sm text-danger" role="alert">{err}</p>}
        <button onClick={submit} disabled={submitting}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-3 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : current?.method === 'mpesa' ? <Smartphone className="h-4 w-4" /> : <Landmark className="h-4 w-4" />}
          {t('billing_pay.send')}
        </button>
        {current?.method === 'mpesa' && <p className="mt-2 text-center text-xs text-muted-foreground">{t('billing_pay.send_hint_mpesa')}</p>}
      </>
    );
  } else {
    const isMpesa = current?.method === 'mpesa';
    bodyEl = (
      <div className="py-2 text-center">
        {view.kind === 'paid' ? (
          <>
            <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-success" />
            <p className="text-sm font-medium text-foreground">{t('billing_pay.paid')}</p>
          </>
        ) : view.kind === 'failed' ? (
          <>
            <XCircle className="mx-auto mb-3 h-10 w-10 text-danger" />
            <p className="text-sm font-medium text-foreground">{t('billing_pay.failed')}</p>
            {view.reason && <p className="mt-1 text-sm text-muted-foreground" role="alert">{view.reason}</p>}
            <button onClick={retry} className="mt-4 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600">{t('billing_pay.try_again')}</button>
          </>
        ) : view.kind === 'confirming' ? (
          <>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-warning-soft">
              <AlertTriangle className="h-6 w-6 text-warning-strong" />
            </div>
            <p className="text-sm font-medium text-foreground">{t('billing_pay.unknown')}</p>
            <p className="mt-2 text-xs text-muted-foreground">{t('billing_pay.unknown_sub')}</p>
          </>
        ) : (
          <>
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-500/[0.12]">
              <Loader2 className="h-6 w-6 animate-spin text-orange-600 dark:text-orange-300" />
            </div>
            <p className="text-sm font-medium text-foreground">{isMpesa ? t('billing_pay.pending_mpesa') : (note || t('billing_pay.pending_bank'))}</p>
            {isMpesa && note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
            <p className="mt-2 text-xs text-muted-foreground">
              {stillWaiting ? t('billing_pay.still_waiting') : isMpesa ? t('billing_pay.pending_mpesa_sub') : t('billing_pay.pending_generic_sub')}
            </p>
          </>
        )}
        {view.kind !== 'failed' && (
          <button onClick={close} className="mt-4 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted">{t('billing_pay.close')}</button>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={close}>
      <div className="w-full max-w-sm rounded-2xl bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-foreground">{t('billing_pay.title', { number: invoice.invoiceNumber })}</h3>
            <p className="text-sm text-muted-foreground">{t('billing_pay.due', { amount: money(invoice.balanceDue, invoice.currency) })}</p>
          </div>
          <button onClick={close} aria-label={t('billing_pay.close')} className="rounded-lg p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
        {bodyEl}
      </div>
    </div>
  );
};

export default PayInvoiceDialog;
